/**
 * SurveyBuilderQuickTour
 *
 * P_SURVEYS_GUIDANCE_PARITY_1.0 — Quick Tour modal for the Survey
 * Editor. Shown the first time an author opens the editor (per
 * browser); dismissible and remembered in localStorage. Authors can
 * re-open it any time via the "Quick tour" button in the header.
 *
 * Vocabulary discipline:
 *   - Speak in business outcomes ("show a different follow-up for
 *     each customer answer", "see how customers move through the
 *     survey") — NOT engine vocabulary ("node", "next_step",
 *     "topology", "rejoin point").
 *   - Stay synchronized with the operational seeds in
 *     `backend/operational_base/wave5_chunk_5a_surveys_branching_seed.py`
 *     so Tooltips, Quick Tour, Help & Insights and Operational
 *     Manuals never disagree.
 *
 * Tri-lingual at ship — EN/FR/ES populated via i18next.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight, Compass, Sparkles, X } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

// Bumping this version forces the tour to re-appear once for every
// author the next time they open the editor. Use sparingly — only
// when steps change materially.
const TOUR_VERSION = 1;
const STORAGE_KEY = `yulontap.surveys.quickTour.seen.v${TOUR_VERSION}`;

const hasSeen = () => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const markSeen = () => {
    try {
        window.localStorage.setItem(STORAGE_KEY, 'true');
    } catch {
        /* no-op — incognito or storage disabled */
    }
};

// Each step intentionally mirrors a registered Help & Insights key so
// the tour, the in-page tooltip, and the operational manual all use
// the same business-outcome wording. If a step text changes here it
// MUST also be updated in the corresponding operational seed.
const STEPS = [
    {
        id: 'write-questions',
        related_key: 'surveys.workflow.question-text',
        icon: '✏️',
        titleKey: 'surveys.quickTour.step1.title',
        titleDefault: 'Write questions in your language',
        bodyKey: 'surveys.quickTour.step1.body',
        bodyDefault:
            'Type each question in the language you author in. The platform translates the others automatically when you save, so customers always see the survey in their own language.',
    },
    {
        id: 'branching',
        related_key: 'surveys.workflow.branching-choices',
        icon: '🌿',
        titleKey: 'surveys.quickTour.step2.title',
        titleDefault: 'Ask different follow-ups per answer',
        bodyKey: 'surveys.quickTour.step2.body',
        bodyDefault:
            'Use Multiple Choice (branching) when a follow-up should depend on the customer\'s answer — for example, asking only Laval customers about Laval. The shortcut "Create New Branch" wires the follow-up to the chosen answer for you.',
    },
    {
        id: 'after-question',
        related_key: 'surveys.workflow.after-question',
        icon: '🎯',
        titleKey: 'surveys.quickTour.step3.title',
        titleDefault: 'Decide what happens after each question',
        bodyKey: 'surveys.quickTour.step3.body',
        bodyDefault:
            'On every question, choose what comes next: continue to the next question, jump to a specific one, or finish the survey. This keeps each customer\'s journey clean — no accidental detours into questions meant for someone else.',
    },
    {
        id: 'flow-map',
        related_key: 'surveys.workflow.flow-map',
        icon: '🗺️',
        titleKey: 'surveys.quickTour.step4.title',
        titleDefault: 'Read the survey path before publishing',
        bodyKey: 'surveys.quickTour.step4.body',
        bodyDefault:
            'The Survey path at the top of the Questions tab shows the full customer journey at a glance. Every branch should end with a checkmark. If you see a warning, follow it — the platform will suggest the fix.',
    },
    {
        id: 'preview',
        related_key: 'surveys.workflow.tab-closing',
        icon: '👀',
        titleKey: 'surveys.quickTour.step5.title',
        titleDefault: 'Preview, set the closing page, then publish',
        bodyKey: 'surveys.quickTour.step5.body',
        bodyDefault:
            'Use the live preview to walk through the survey as a customer would. Configure the Closing page to leave guests with a warm thank-you (and optionally invite them to leave a Google review). When everything reads well, save — the public link is ready to share.',
    },
];

const SurveyBuilderQuickTour = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [index, setIndex] = useState(0);

    // Reset to step 1 every time the modal is opened so re-openings
    // feel intentional (the author asked to see it again).
    useEffect(() => {
        if (open) setIndex(0);
    }, [open]);

    const step = STEPS[index];
    const isFirst = index === 0;
    const isLast = index === STEPS.length - 1;

    const dots = useMemo(() => STEPS.map((_, i) => i), []);

    const handleClose = () => {
        markSeen();
        if (typeof onClose === 'function') onClose();
    };

    const handlePrev = () => setIndex((i) => Math.max(0, i - 1));
    const handleNext = () => {
        if (isLast) {
            handleClose();
        } else {
            setIndex((i) => Math.min(STEPS.length - 1, i + 1));
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
            <DialogContent
                className="max-w-lg"
                data-testid="survey-quick-tour-modal"
                aria-describedby={undefined}
            >
                <DialogHeader>
                    <div className="flex items-center gap-2 text-fuchsia-600 dark:text-fuchsia-300 text-xs font-semibold uppercase tracking-wide">
                        <Compass className="w-3.5 h-3.5" />
                        <span>
                            {t('surveys.quickTour.eyebrow', 'Quick tour · {{current}} of {{total}}', {
                                current: index + 1,
                                total: STEPS.length,
                            })}
                        </span>
                    </div>
                    <DialogTitle className="text-lg flex items-center gap-2" data-testid="survey-quick-tour-title">
                        <span aria-hidden="true">{step.icon}</span>
                        <span>{t(step.titleKey, step.titleDefault)}</span>
                    </DialogTitle>
                </DialogHeader>

                <DialogDescription asChild>
                    <p
                        className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed"
                        data-testid="survey-quick-tour-body"
                    >
                        {t(step.bodyKey, step.bodyDefault)}
                    </p>
                </DialogDescription>

                {/* Dots */}
                <div className="flex items-center justify-center gap-1.5 pt-1" data-testid="survey-quick-tour-dots">
                    {dots.map((i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setIndex(i)}
                            aria-label={t('surveys.quickTour.goToStep', 'Go to step {{n}}', { n: i + 1 })}
                            className={`h-1.5 rounded-full transition-all ${
                                i === index
                                    ? 'w-6 bg-fuchsia-500'
                                    : 'w-1.5 bg-slate-300 dark:bg-slate-600 hover:bg-slate-400'
                            }`}
                            data-testid={`survey-quick-tour-dot-${i}`}
                        />
                    ))}
                </div>

                {/* Footer controls */}
                <div className="flex items-center justify-between gap-2 pt-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleClose}
                        data-testid="survey-quick-tour-skip"
                    >
                        <X className="w-3.5 h-3.5 mr-1" />
                        {t('surveys.quickTour.skip', 'Skip tour')}
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handlePrev}
                            disabled={isFirst}
                            data-testid="survey-quick-tour-prev"
                        >
                            <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                            {t('surveys.quickTour.back', 'Back')}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleNext}
                            data-testid="survey-quick-tour-next"
                        >
                            {isLast ? (
                                <>
                                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                                    {t('surveys.quickTour.finish', 'Start authoring')}
                                </>
                            ) : (
                                <>
                                    {t('surveys.quickTour.next', 'Next')}
                                    <ArrowRight className="w-3.5 h-3.5 ml-1" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SurveyBuilderQuickTour;
export { hasSeen as quickTourHasSeen, markSeen as quickTourMarkSeen };
